// ============================================================================
// quote-outcome.js — single source of truth for a project's win/lost outcome
// ----------------------------------------------------------------------------
// Background (CRM data-integrity fix, 2026-06-22): `projects` carries TWO
// outcome columns written by different code paths that never cross:
//
//   • projects.status       — a CHECK-constrained PIPELINE-STAGE column
//                             (draft/quoted/sent/won/lost/…). The Stage 5
//                             "Mark Won/Lost" buttons write status='won'/'lost'.
//   • projects.quote_status — the OUTCOME column (pending/won/lost/expired,
//                             DEFAULT 'pending'). AllQuotesPage writes ONLY this
//                             via PATCH /projects/:id/quote-status.
//
// Six readers each rolled their own status logic. Two trusted `status` (blind
// to quote_status); four preferred `quote_status` but let its truthy default
// 'pending' SHADOW a legacy status='won' — so each camp missed half the wins.
// The whole backend (analytics views, company/property stats, gold-standards
// exemplar filter) reads quote_status, so Stage-5 wins were invisible to every
// analytic and headline win-rates were understated.
//
// This helper is the ONE place that decides a project's outcome. It is
// ADDITIVE: it recognises a win recorded on EITHER column, so adopting it can
// only add win recognition, never un-count one — except a genuine
// direction-conflict row (status='won' but quote_status='lost', i.e. a later
// AllQuotes flip), where the canonical quote_status correctly wins.
//
// Adopt this everywhere instead of re-deriving status. The companion flag
// `qm_canonical_quote_status` (feature-flags.js) gates the Stage-5 *write* that
// populates quote_status going forward; this reader stays on regardless.
// ============================================================================

/**
 * Resolve a project's outcome/pipeline state from either column shape.
 *
 * Accepts both the apiFetch-camel shape (`quoteStatus`) and the raw snake shape
 * (`quote_status` / `status` / `email_sent` …), so it works on projects loaded
 * via apiFetch AND on raw backend rows.
 *
 * Precedence:
 *   1. Canonical `quote_status` when it carries a real decision (won/lost/expired).
 *   2. Legacy Stage-5 signal: in-session `wonAt`/`lostAt`, else `status` won/lost.
 *      ('pending' on quote_status does NOT reach here — it is not a decision, so
 *      it never shadows a legacy status='won'.)
 *   3. Pipeline-stage inference (sent → quoted → draft).
 *
 * @param {object} p — a project record (camel or snake keyed)
 * @returns {'won'|'lost'|'expired'|'sent'|'quoted'|'draft'}
 */
export function getQuoteOutcome(p: Record<string, unknown> | null | undefined): 'won' | 'lost' | 'expired' | 'sent' | 'quoted' | 'draft' {
    if (!p) return 'draft';

    // 1. Canonical outcome column — only when it carries an actual decision.
    //    'pending'/null fall through (NOT a decision → must not shadow status).
    const qs = String(p.quoteStatus ?? p.quote_status ?? '').toLowerCase();
    if (qs === 'won' || qs === 'lost' || qs === 'expired') return qs;

    // 2. Legacy Stage-5 signal: optimistic in-session timestamps, then the
    //    pipeline-stage column's won/lost values.
    const status = String(p.status ?? '').toLowerCase();
    if (p.wonAt || status === 'won') return 'won';
    if (p.lostAt || status === 'lost') return 'lost';

    // 3. Pipeline-stage inference for undecided rows.
    if (p.emailSent || p.email_sent || p.emailSentAt || p.email_sent_at || status === 'sent') return 'sent';
    if (p.pdfGenerated || p.pdf_generated || p.quoteNumber || p.quote_number || status === 'quoted') return 'quoted';
    return 'draft';
}

/** Convenience: did this project win? (canonical-aware). */
export function isWonOutcome(p: Record<string, unknown> | null | undefined): boolean {
    return getQuoteOutcome(p) === 'won';
}

/** Convenience: was this project lost? (canonical-aware). */
export function isLostOutcome(p: Record<string, unknown> | null | undefined): boolean {
    return getQuoteOutcome(p) === 'lost';
}
